import React, { useState, useEffect, Fragment, useMemo } from "react";
import Layout from "components/sales/layout/layout";
import localization from "assets/localization/localization";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import ModalPop from "components/sales/modal/modal";
import DropDownInput from "components/sales/Input/dropDownInput/dropDownInput";
import "./ViewTagOrder.css";

const ViewTagOrder = ({ getToaTagOrders, toaTagOrders, approveTagOrder, getUserAnalytics, userAnalytics }) => {
  const { id } = useParams();
  const role_id = Number(localStorage.getItem("fusion-role"));

  const [isLoading, setIsLoading] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressData, setAddressData] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [originalAddress, setOriginalAddress] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [originalOrderItems, setOriginalOrderItems] = useState([]);

  const rejectionReasons = [
    { id: "Non-Serviceable Area", name: "Non-Serviceable Area" },
    { id: "Incorrect Quantity", name: "Incorrect Quantity" },
    { id: "Stock Unavailable", name: "Stock Unavailable" },
    { id: "Duplicate Request", name: "Duplicate Request" },
    { id: "Tags Not Required", name: "Tags Not Required" },
    { id: "User Churn", name: "User Churn" }
  ];

  useEffect(() => {
    if (getToaTagOrders && id) {
      (async () => {
        setIsLoading(true);
        await getToaTagOrders(null, 1, id);
        setIsLoading(false);
      })();
    }
  }, [id]);

  const orderData = toaTagOrders?.results?.[0];

  useEffect(() => {
    if (orderData?.address) {
      setAddressData(orderData.address);
      setOriginalAddress(orderData.address);
    }

    if (orderData?.order_listing_summary) {
      setOrderItems(orderData.order_listing_summary);
      setOriginalOrderItems(orderData.order_listing_summary);
    }
  }, [orderData]);

  useEffect(() => {
    if (orderData?.requestor?.user_id && getUserAnalytics) {
      getUserAnalytics({ user_id: orderData.requestor.user_id });
    }
  }, [orderData?.requestor?.user_id, getUserAnalytics]);

  const hasOriginalQuantity = (originalQuantity) =>
    originalQuantity != null && originalQuantity !== 0;

  const formatQuantityWithOriginal = (quantity, originalQuantity) => {
    if (hasOriginalQuantity(originalQuantity)) {
      return `${quantity} (${originalQuantity})`;
    }
    return quantity ?? "N/A";
  };

  const formatAddressText = (addr) => {
    if (addr == null || addr === "") return null;
    if (typeof addr === "string") return addr;
    if (typeof addr !== "object") return String(addr);

    const line = [addr.address, addr.locality].filter(Boolean).join(", ");
    const cityLine = [addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
    return [line, cityLine].filter(Boolean).join(" — ") || null;
  };

  const displayCellValue = (value) => {
    if (value == null || value === "") return "N/A";
    if (React.isValidElement(value)) return value;
    if (typeof value === "object") return formatAddressText(value) || "N/A";
    return value;
  };

  const renderRow = (label, value) => (
    <div className="detail-item" key={label}>
      <span className="label">{label}</span>
      <span className="value">{displayCellValue(value)}</span>
    </div>
  );

  /* ---------------- ADDRESS HANDLING ---------------- */

  const handleAddressChange = (key, value) => {
    setAddressData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddressCancel = () => {
    setAddressData(originalAddress);
    setIsEditingAddress(false);
  };

  const handleAddressDone = () => {
    setIsEditingAddress(false);
  };

  useEffect(() => {
    if (orderData?.order_listing_summary) {
      setOrderItems(orderData.order_listing_summary);
      setOriginalOrderItems(
        JSON.parse(JSON.stringify(orderData.order_listing_summary))
      );
    }
  }, [orderData]);

  const renderAddressField = (label, key) => (
    <div className="detail-item" key={key}>
      <span className="label">{label}</span>
      <span className="value">
        {isEditingAddress ? (
          <input
            className="form-control"
            value={addressData?.[key] || ""}
            onChange={(e) => handleAddressChange(key, e.target.value)}
          />
        ) : (
          displayCellValue(addressData?.[key])
        )}
      </span>
    </div>
  );

  /* ---------------- ORDER HANDLING ---------------- */

  const handleQuantityChange = (index, value) => {
    const updated = [...orderItems];
    const maxQty = originalOrderItems[index].quantity;

    if (value < 0) value = 0;
    if (value > maxQty) value = maxQty;

    updated[index].quantity = value;
    setOrderItems(updated);
  };

  const handleOrderCancel = () => {
    setOrderItems(originalOrderItems);
    setIsEditingOrder(false);
  };

  const handleOrderDone = () => {
    setIsEditingOrder(false);
  };

  const renderOrderSection = () => {
    if (!orderItems.length) return null;

    return (
      <div className="details-card">
        <div className="details-card-header d-flex justify-content-between align-items-center">
          <span>Bank Details</span>

          {orderData?.status === "CREATED" && (
            <div className="d-flex gap-2">
              {!isEditingOrder ? (
                <Button
                  size="sm"
                  className="btn-outline-primary btn-transparent"
                  onClick={() => setIsEditingOrder(true)}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    className="btn-success btn-transparent"
                    onClick={handleOrderDone}
                  >
                    Done
                  </Button>

                  <Button
                    size="sm"
                    className="btn-outline-secondary btn-transparent"
                    onClick={handleOrderCancel}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="details-card-body">
          {orderItems.map((bank, index) => {
            const originalQty = originalOrderItems[index]?.quantity;

            return (
              <div
                key={index}
                className="order-card d-flex justify-content-between align-items-center mb-3"
                style={{ background: "rgb(250, 250, 250)" }}
              >
                <div>
                  <div className="bank-name">{bank.bank_name}</div>
                  <div className="tag-class">
                    Tag Class: {bank.tag_class_value}
                  </div>
                </div>

                {isEditingOrder ? (
                  <div
                    className="d-flex align-items-center"
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      overflow: "hidden",
                      height: "36px"
                    }}
                  >
                    <input
                      type="number"
                      value={bank.quantity ?? ""}
                      min={0}
                      max={originalQty}
                      onChange={(e) => {
                        const val = e.target.value;

                        if (val === "") {
                          const updated = [...orderItems];
                          updated[index].quantity = "";
                          setOrderItems(updated);
                          return;
                        }

                        handleQuantityChange(index, Number(val));
                      }}
                      style={{
                        width: "60px",
                        border: "none",
                        outline: "none",
                        textAlign: "center"
                      }}
                    />

                    <button
                      style={{
                        width: "36px",
                        border: "none",
                        background: "#f3f4f6",
                        fontSize: "18px",
                        cursor: "pointer"
                      }}
                      disabled={bank.quantity <= 0}
                      onClick={() =>
                        handleQuantityChange(index, bank.quantity - 1)
                      }
                    >
                      −
                    </button>

                  </div>

                ) : (
                  <div className="qty-readonly text-end">
                    <div>{bank.quantity}</div>
                    {hasOriginalQuantity(bank.original_ordered_quantity) && (
                      <div className="original-qty">
                        ({bank.original_ordered_quantity})
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ---------------- APPROVE / REJECT ---------------- */

  const approveOrder = async (status) => {
    try {

      const invalidItem = orderItems.find(
        (item) =>
          item.quantity === "" ||
          item.quantity === null ||
          Number.isNaN(Number(item.quantity))
      );

      if (invalidItem) {
        toast.error("Quantity cannot be empty");
        return;
      }

      setIsLoading(true);

      const payload = {
        order_id: id,
        status,
        data_version: orderData?.data_version,
        address: addressData,
        order_item_details: orderItems
      };

      if (status === "REJECTED") {
        payload.rejection_reason = rejectionReason;
      }

      await approveTagOrder(payload);

      toast.success(`Order ${status.toLowerCase()} successfully`);
      setShowRejectModal(false);
      setRejectionReason("");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      window.history.back();
    }
  };

  const unactivatedClasses = useMemo(() => {
    const classes = new Set();
    userAnalytics?.unactivated_inventory?.forEach((item) =>
      item.tag_classes?.forEach((tc) => classes.add(tc.tag_class))
    );
    return Array.from(classes).sort((a, b) => a - b);
  }, [userAnalytics]);

  const issuanceClasses = useMemo(() => {
    const classes = new Set();
    userAnalytics?.issuance_done?.forEach((item) =>
      item.tag_classes?.forEach((tc) => classes.add(tc.tag_class))
    );
    return Array.from(classes).sort((a, b) => a - b);
  }, [userAnalytics]);

  const headerBtns = () => (
    <Fragment>
      <Button onClick={() => approveOrder("APPROVED")}>
        Approve
      </Button>
      <Button onClick={() => setShowRejectModal(true)}>
        Reject
      </Button>
    </Fragment>
  );

  const renderInventoryTable = (items, classList) => (
    <div className="activity-table-wrap">
      <table className="activity-data-table">
        <thead>
          <tr>
            <th className="acquirer-col">Acquirer</th>
            {classList.map((cls) => (
              <th key={cls} className="class-col">Class {cls}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items?.length > 0 ? (
            items.map((item, idx) => (
              <tr key={idx}>
                <td className="acquirer-col">{item.acquirer}</td>
                {classList.map((cls) => (
                  <td key={cls} className="class-col">
                    {item.tag_classes?.find((tc) => tc.tag_class === cls)?.total_count ?? "-"}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={Math.max(classList.length + 1, 1)} className="empty-cell">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderUserAnalytics = () => {
    const lastOrders = userAnalytics?.last_3_order_details || [];

    return (
      <div className="activity-history-panel">
        <div className="activity-block">
          <div className="activity-block-header">
            <h5 className="activity-block-title">
              Unactivated inventory assigned to the user in the last 6 months
            </h5>
          </div>
          <div className="activity-block-content">
            {renderInventoryTable(userAnalytics?.unactivated_inventory, unactivatedClasses)}
          </div>
        </div>

        <div className="activity-block">
          <div className="activity-block-header">
            <h5 className="activity-block-title">
              Issuance in the last 30 days by this user
            </h5>
          </div>
          <div className="activity-block-content">
            {renderInventoryTable(userAnalytics?.issuance_done, issuanceClasses)}
          </div>
        </div>

        <div className="activity-block">
          <div className="activity-block-header">
            <h5 className="activity-block-title">Last 3 orders details</h5>
          </div>
          <div className="activity-block-content">
            {lastOrders.length > 0 ? (
              <div className="past-orders-list">
                {lastOrders.map((order, idx) => {
                  const addressText = formatAddressText(order.address);

                  return (
                    <div key={order.order_id || idx} className="past-order-card">
                      <div className="past-order-header">
                        <span className="past-order-status">
                          {order.order_status || "N/A"}
                        </span>
                        <span className="past-order-date">
                          {order.order_date || "-"}
                        </span>
                      </div>

                      <div className="past-order-fields">
                        <div className="past-order-field">
                          <span className="field-label">Order ID</span>
                          <span className="field-value">#{order.order_id}</span>
                        </div>
                        <div className="past-order-field">
                          <span className="field-label">Total Quantity</span>
                          <span className="field-value field-value-highlight">
                            {order.total_quantity ?? "-"}
                          </span>
                        </div>
                      </div>

                      {order.bank_wise_items?.length > 0 && (
                        <div className="bank-breakdown-block">
                          <span className="bank-breakdown-label">
                            Bank wise breakdown
                          </span>
                          {order.bank_wise_items.map((bank, bIdx) => (
                            <div key={bIdx} className="bank-breakdown-row">
                              <span className="bank-name-chip">{bank.bank_name}</span>
                              <div className="tag-class-chips">
                                {bank.tag_classes?.map((tc, tIdx) => (
                                  <span key={tIdx} className="tag-badge">
                                    C{tc.tag_class}: {tc.total_count}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {addressText && (
                        <div className="order-address-full">
                          <span className="field-label">Delivery Address</span>
                          <p className="order-address-text">{addressText}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="activity-empty-state">No orders found</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Layout
        title={localization.BacktoList}
        isLoading={isLoading}
        Buttons={headerBtns}
        isGoback
      >
        {orderData && (
          <div className="vendor-details-wrapper">
            <div className="details-card">
              <div className="details-card-header">
                Tag Order Details
              </div>
              <div className="details-card-body">
                {renderRow("Order ID", orderData.order_id)}
                {renderRow("Status", orderData.status)}
                {renderRow(
                  "Total Quantity",
                  formatQuantityWithOriginal(
                    orderData.quantity,
                    orderData.original_ordered_quantity
                  )
                )}
                {renderRow("Total Amount", orderData.amount)}
                {renderRow("Creation Date", orderData.date)}
                {renderRow("Requestor Available Inventory", orderData?.available_quantity)}
                {orderData.status === "REJECTED" && renderRow("Rejection Reason", orderData?.rejection_reason)}
                {orderData.courier_partner && renderRow("Courier Partner", orderData?.courier_partner)}
                {orderData.docket_number && renderRow("Docket Number", orderData?.docket_number)}
                {
                  orderData.tracking_link &&
                    renderRow(
                    "",
                    <a href={orderData.tracking_link} target="_blank">
                    Tracking Link  🚚
                    </a>
                  )
                }
              </div>
            </div>
            {addressData && (
              <div className="details-card">
                <div className="details-card-header d-flex justify-content-between align-items-center">
                  <span>Address Details</span>
                  {orderData?.status === "CREATED" && (
                    <div>
                      {!isEditingAddress ? (
                        <Button size="sm" className=" btn-transparent" onClick={() => setIsEditingAddress(true)}>
                          Edit
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="btn-transparent"
                            onClick={handleAddressDone}
                          >
                            Done
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="btn-transparent"
                            onClick={handleAddressCancel}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="details-card-body">
                  {renderAddressField("City", "city")}
                  {renderAddressField("Locality", "locality")}
                  {renderAddressField("Pincode", "pincode")}
                  {renderAddressField("State", "state")}
                </div>
              </div>
            )}

            {renderOrderSection()}

            {orderData?.requestor && (
              <div className="details-card">
                <div className="details-card-header">
                  Requestor Details
                </div>
                <div className="details-card-body">
                  {renderRow(
                    "Name",
                    `${orderData?.requestor?.first_name || ""} ${
                      orderData?.requestor?.last_name || ""
                    }`.trim()
                  )}
                  {renderRow("Phone", orderData?.requestor?.phone)}
                  {renderRow("Role", orderData?.requestor?.role)}
                  {renderRow("User ID", orderData?.requestor?.user_id)}
                </div>
              </div>
            )}

            {orderData?.requestor && (
              <div className="details-card">
                <div className="details-card-header">
                  Requestor Activity History
                </div>
                <div className="details-card-body activity-history-body">
                  {renderUserAnalytics()}
                </div>
              </div>
            )}

          </div>
        )}
      </Layout>
      <ModalPop
        title={localization.conf}
        show={showRejectModal}
        handleModalHide={() => {
          setShowRejectModal(false);
          setRejectionReason("");
        }}
      >
        <div className="text-center">
          <h4>Are you sure you want to reject this order?</h4>

          <div className="col-md-6 mt-4 mx-auto">
            <DropDownInput
              label="Rejection Reason"
              data={rejectionReasons}
              required
              isStarRequired
              getIdValue={(val) => setRejectionReason(val.id)}
            />
          </div>

          <div className="d-flex justify-content-center mt-5">
            <Button
              className="btn btn-danger mr-3"
              onClick={() => setShowRejectModal(false)}
            >
              No
            </Button>

            <Button
              className="btn btn-success ml-3"
              disabled={!rejectionReason}
              onClick={() => approveOrder("REJECTED")}
            >
              Yes
            </Button>
          </div>
        </div>
      </ModalPop>
    </>
  );
};

export default ViewTagOrder;